/**
 * Защита звукового менеджера Phaser от обращения к ЗАКРЫТОМУ AudioContext.
 *
 * В Phaser 3.90 WebAudioSoundManager.onGameVisible (появился в 3.88 как
 * обходной путь для iOS 17/18) по событию VISIBLE ставит таймер на 100мс и
 * внутри него дергает context.suspend() + context.resume(), проверяя только
 * что ссылка на контекст не пустая. onBlur так же зовет suspend() без проверки
 * состояния.
 *
 * В SPA игра пересоздается (демо-партия меню -> матч, двойной монтаж
 * StrictMode в dev), и разобранная игра закрывает свой контекст
 * (context.close() в destroy). Игровой цикл в фоновой вкладке стоит вместе с
 * requestAnimationFrame, поэтому отложенный разбор такой игры выполняется
 * ровно в момент возврата на вкладку — в то же окно в 100мс, что и таймер
 * onGameVisible. Таймер срабатывает уже на закрытом контексте, suspend()
 * бросает InvalidStateError («Cannot suspend a closed AudioContext»), и в
 * dev-режиме поверх приложения встает красный оверлей ошибок CRA.
 *
 * Чиним не отключением механики (она нужна для звука на iOS), а проверкой
 * состояния контекста В МОМЕНТ обращения, а не в момент планирования.
 *
 * @param {Phaser.Game} game
 */
// suspend()/resume() возвращают промис и на закрытом контексте не бросают, а
// ОТКЛОНЯЮТ его. Контекст успевает закрыться и между проверкой состояния и
// самим вызовом (игра разбирается ровно в этот момент), поэтому одной проверки
// мало: без перехвата отказ всплывает необработанным промисом — красный
// оверлей CRA в разработке и событие ошибки в аналитике на проде
const callSafely = (contextMethod) => {
  try {
    const result = contextMethod();
    if (result && typeof result.catch === 'function') {
      result.catch(() => {
      });
    }
  } catch (error) {
    // Синхронный InvalidStateError на старых движках — тоже не наша забота
  }
};

// Phaser дергает suspend()/resume() из нескольких мест: обработчики фокуса и
// видимости, разблокировка звука по первому касанию (unlock ставит свои
// слушатели на document), разбор игры. Подменять каждое место по отдельности —
// бесконечная гонка, поэтому предохранитель ставится на сами методы контекста:
// на закрытом контексте они становятся безобидным no-op, а отказ промиса
// гасится в любом случае
const guardContextMethod = (context, methodName) => {
  const original = context[methodName];
  if (typeof original !== 'function' || original.__isGuarded) {
    return;
  }
  const guarded = function guardedContextMethod() {
    if (context.state === 'closed') {
      return Promise.resolve();
    }
    try {
      const result = original.call(context);
      return result && typeof result.catch === 'function'
        ? result.catch(() => {
        })
        : Promise.resolve();
    } catch (error) {
      // Синхронный InvalidStateError на старых движках
      return Promise.resolve();
    }
  };
  guarded.__isGuarded = true;
  context[methodName] = guarded;
};

export const guardPhaserAudioContext = (game) => {
  const soundManager = game?.sound;
  // У NoAudioSoundManager и HTML5-менеджера этих методов/контекста нет
  if (!soundManager || !soundManager.context) {
    return;
  }

  guardContextMethod(soundManager.context, 'suspend');
  guardContextMethod(soundManager.context, 'resume');
  // close() тоже зовется повторно: витрина пересоздает игру, и разбор старой
  // может наложиться на уже закрытый контекст
  guardContextMethod(soundManager.context, 'close');

  soundManager.onGameVisible = function onGameVisibleSafe() {
    const context = this.context;

    // Задержка и пара suspend/resume — как в оригинале Phaser: без них на iOS
    // остаются артефакты звука после возврата в приложение
    window.setTimeout(() => {
      if (!context || context.state === 'closed') {
        return;
      }
      callSafely(() => context.suspend());
      callSafely(() => context.resume());
    }, 100);
  };

  soundManager.onBlur = function onBlurSafe() {
    const context = this.context;
    if (this.locked || !context || context.state === 'closed') {
      return;
    }
    callSafely(() => context.suspend());
  };
};
